import { FC, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { MiBuildHouseAlt } from "@miplatform/assets";
import { MiAlert, MiPlaceholderArea } from "@miplatform/components";
import { ButtonVariants } from "@miplatform/types";
import { mdiPlus } from "@mdi/js";
import { useTheme } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { ICalendarSetData } from "../../ConfigCalendarSetList.types";
import { CalendarUserCard } from "./CalendarUserCard";

export const CalendarWorkCapacityListTabView: FC<
  CalendarWorkCapacityListTabViewProps
> = ({
  form,
  extraTabProps: { isReadOnly, onTabChange, onAddUser } = {},
}: CalendarWorkCapacityListTabViewProps) => {
  const theme = useTheme();
  const { watch } = form;
  const assignableUsers = watch("assignableUsers");

  const sortedAssignableUsers = useMemo(
    () =>
      [...assignableUsers].sort((a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name);
        }
        return 0;
      }),
    [assignableUsers]
  );

  return (
    <ConfigAccordionHeader title="Daily Work Capacity">
      {assignableUsers.length === 0 ? (
        <MiPlaceholderArea
          text="No Assignable Users"
          helperText="Tip: assign users to see them here."
          iconComponent={<img src={MiBuildHouseAlt} alt="House Icon" />}
          containerStyle={{
            backgroundColor: theme.palette.primary.light,
            height: "100%",
            minHeight: 450,
          }}
          buttons={
            !isReadOnly
              ? [
                  {
                    title: "Add Assignable Users",
                    startIcon: mdiPlus,
                    variant: ButtonVariants.Contained,
                    onClick: onAddUser,
                  },
                ]
              : []
          }
        />
      ) : (
        <>
          {sortedAssignableUsers.map((user) => (
            <CalendarUserCard
              key={user.id}
              user={user}
              isReadOnly={isReadOnly}
              onEditClick={(user) => onTabChange?.(user.id)}
            />
          ))}
          <MiAlert
            description="Click on a user to configure the work capacity or click next to continue."
            sx={{ mt: 2, py: 0, px: 2 }}
          />
        </>
      )}
    </ConfigAccordionHeader>
  );
};

interface CalendarWorkCapacityListTabViewProps {
  form: UseFormReturn<ICalendarSetData>;
  extraTabProps?: {
    isReadOnly?: boolean;
    onTabChange?: (id: string) => void;
    onAddUser?: () => void;
  };
}
